import AuthenticationControllerABI from "@config/abi/AuthenticationController";
import StratosphereABI from "@config/abi/Stratosphere";
import { ADDRESS_ZERO, ZERO } from "@config/constants";
import useActiveWagmi from "@hooks/useActiveWagmi";
import { useWriteContractWithTransactionWatch } from "@hooks/useContract";
import {
	getStratosphereAddress,
	getStratosphereAuthenticationControllerAddress,
} from "@utils/addressHelpers";
import { useCommify } from "@vaporfi/hooks";
import { useCallback, useMemo } from "react";
import type { Address } from "viem";
import { useReadContract, useReadContracts } from "wagmi";
export const useStratosphereTokenIdOf = (chainIdOverrides?: number) => {
	const { account, chainId: wagmiChainId, isConnected } = useActiveWagmi();
	const chainId = chainIdOverrides ?? wagmiChainId;

	const stratosphereContract = {
		abi: StratosphereABI,
		address: getStratosphereAddress(chainId),
	} as const;

	const authenticationControllerContract = {
		abi: AuthenticationControllerABI,
		address: getStratosphereAuthenticationControllerAddress(chainId),
	} as const;

	const { data, error, isLoading, ...rest } = useReadContracts({
		allowFailure: true,
		//@ts-ignore
		contracts: [
			{
				...stratosphereContract,
				args: [account],
				functionName: "tokenIdOf",
			},
			{
				...authenticationControllerContract,
				args: [account],
				functionName: "authorizedTokenOf",
			},
		],
	});

	const tokenId = useMemo(() => {
		if (isConnected) {
			const firstData =
				typeof data?.[0] === "object" ? data?.[0].result : data?.[0];
			return firstData === ZERO ? data?.[1].result : firstData;
		}
		return BigInt(0);
	}, [isConnected, data]);

	return { data: tokenId, error, isLoading, ...rest };
};

export const useRegistrationOwnerOf = (
	tokenId: string,
	account: string = ADDRESS_ZERO,
) => {
	const { chainId } = useActiveWagmi();
	const stratosphereContract = {
		abi: StratosphereABI,
		address: getStratosphereAddress(chainId),
	};

	const res = useReadContract({
		...stratosphereContract,
		args: [BigInt(tokenId)],
		functionName: "ownerOf",
	});

	return {
		isOwner: res?.data && account && res?.data?.toString?.() === account,
		...res,
	};
};

export const useRegistrationAuthorizedAccountsOf = (tokenId: string) => {
	const { chainId } = useActiveWagmi();
	const authenticationControllerContract = {
		abi: AuthenticationControllerABI,
		address: getStratosphereAuthenticationControllerAddress(chainId),
	};

	const { data: _data, ...rest } = useReadContract({
		...authenticationControllerContract,
		args: [BigInt(tokenId)],
		functionName: "authorizedAccountsOf",
	});

	const data =
		_data && _data.length > 0
			? _data.filter((item) => item !== ADDRESS_ZERO)
			: [];

	return {
		data,
		...rest,
	};
};

export const useRegistrationLinkedAccountsOf = (tokenId: string) => {
	const { chainId } = useActiveWagmi();
	const authenticationControllerContract = {
		abi: AuthenticationControllerABI,
		address: getStratosphereAuthenticationControllerAddress(chainId),
	};

	const { data: _data, ...rest } = useReadContract({
		...authenticationControllerContract,
		args: [BigInt(tokenId)],
		functionName: "linkedAccountsOf",
	});

	const data =
		_data && _data.length > 0
			? _data.filter((item) => item !== ADDRESS_ZERO)
			: [];

	return {
		data,
		...rest,
	};
};

export const useAuthorizedTokenOf = (account: Address) => {
	const { chainId } = useActiveWagmi();
	const authenticationControllerContract = {
		abi: AuthenticationControllerABI,
		address: getStratosphereAuthenticationControllerAddress(chainId),
	};

	return useReadContract({
		...authenticationControllerContract,
		args: [account],
		functionName: "authorizedTokenOf",
	});
};

export const useRegistrationLinkAccount = (tokenId: string) => {
	const { chainId } = useActiveWagmi();
	const authenticationControllerContract = {
		abi: AuthenticationControllerABI,
		address: getStratosphereAuthenticationControllerAddress(chainId),
	} as const;

	const { write, ...rest } = useWriteContractWithTransactionWatch();

	const onLinkAccount = useCallback(async () => {
		return write({
			...authenticationControllerContract,
			args: [BigInt(tokenId ?? "0")],
			functionName: "linkAccount",
		});
	}, [write]);

	return {
		onLinkAccount,
		...rest,
	};
};

export const useRegistrationUnlinkAccount = (tokenId: bigint) => {
	const { chainId } = useActiveWagmi();
	const authenticationControllerContract = {
		abi: AuthenticationControllerABI,
		address: getStratosphereAuthenticationControllerAddress(chainId),
	};

	const { write, ...rest } = useWriteContractWithTransactionWatch();

	const onUnlinkAccount = useCallback(async () => {
		return write({
			...authenticationControllerContract,
			args: [tokenId],
			functionName: "unlinkAccount",
		});
	}, [write]);

	return {
		onUnlinkAccount,
		...rest,
	};
};

export const useRegistrationAuthorizeAccount = (
	tokenId: string,
	secondAccount: Address,
	isAuthorized = true,
) => {
	const { chainId } = useActiveWagmi();
	const authenticationControllerContract = {
		abi: AuthenticationControllerABI,
		address: getStratosphereAuthenticationControllerAddress(chainId),
	};

	const { write, ...rest } = useWriteContractWithTransactionWatch();

	const onAuthorizeAccount = useCallback(async () => {
		return write({
			...authenticationControllerContract,
			args: [BigInt(tokenId), secondAccount, isAuthorized],
			functionName: "authorize",
		});
	}, [write]);

	return {
		onAuthorizeAccount,
		...rest,
	};
};

export const useRegistrationMint = () => {
	const { chainId } = useActiveWagmi();
	const stratosphereContract = {
		abi: StratosphereABI,
		address: getStratosphereAddress(chainId),
	};

	const { write, ...rest } = useWriteContractWithTransactionWatch();

	const onMint = useCallback(async () => {
		return write({
			...stratosphereContract,
			functionName: "mint",
		});
	}, [write]);

	return {
		onMint,
		...rest,
	};
};

export const useMemberCount = (isCommify?: boolean) => {
	const { chainId } = useActiveWagmi();
	const stratosphereContract = {
		abi: StratosphereABI,
		address: getStratosphereAddress(chainId),
	};

	const { data } = useReadContract({
		...stratosphereContract,
		functionName: "totalSupply",
	});
	const commify = useCommify();
	if (isCommify) {
		return commify(data?.toString());
	}
	return data?.toString() || "0";
};

export default useRegistrationMint;
